import { PrimitiveType } from '@/interfaces/common.interfaces';
import { FilterBlocksEnum, IFilterGridItem } from '@/interfaces/constructor.interfaces';
import {
  FilterValuesType,
  IExtendedFilterCollectionItem,
  IFilterPrimitive,
} from '@/interfaces/filters.interfaces';
import instanceOfFilterPrimitive from '@/utils/instanceOfFilterPrimitive';
import instanceOfFilterRange from '@/utils/instanceOfFilterRange';
import instanceOfRange from '@/utils/instanceOfRange';
import instanceOfReference from '@/utils/instanceOfReference';

/**
 * Получить существующие выбранные значения фильтра
 * @param filterValues {array} Выбранные значения фильтра
 * @param availableFilterValues {object} Доступные значения фильтра
 * @param filterGridItem {object} Информация о фильтре
 */
const takeExistingFilterValue = (
  filterValues: any[],
  availableFilterValues?: FilterValuesType,
  filterGridItem?: IFilterGridItem,
) => {
  if (!availableFilterValues || !filterValues.length || !filterGridItem) {
    return;
  }

  // Если фильтр по ключу из query-параметра является Reference
  if (
    Array.isArray(availableFilterValues) &&
    availableFilterValues.length &&
    instanceOfReference(availableFilterValues[0])
  ) {
    const availableFilterItemValues = [
      ...availableFilterValues,
    ] as IExtendedFilterCollectionItem[];

    // Массив значений фильтра, которые существуют в списке возможных значений фильтра
    const existingFilterItemValues: IExtendedFilterCollectionItem['id'][] = [];

    // Проходимся по возможным значениям фильтра
    // для нахождения значений
    availableFilterItemValues.forEach(filterItemValue => {
      // Для reference возможными значениями в query является id или slug
      const value = filterValues.find(
        value => value === filterItemValue.id || value === filterItemValue.slug,
      );
      if (typeof value !== 'undefined') {
        // Если значение является валидным, то
        // необходимо сохранить id найденного значения фильтра
        existingFilterItemValues.push(filterItemValue.id);
      }
    });
    if (existingFilterItemValues.length) {
      switch (filterGridItem.type) {
        case FilterBlocksEnum.FilterSelect:
        case FilterBlocksEnum.FilterSlider: {
          return existingFilterItemValues[existingFilterItemValues.length - 1];
        }
        case FilterBlocksEnum.FilterMultipleSelect:
        case FilterBlocksEnum.FilterLocation:
        case FilterBlocksEnum.FilterCheckboxGroup:
        case FilterBlocksEnum.FilterModelCheckboxGroup:
        case FilterBlocksEnum.FilterColorPicker: {
          return Array.from(new Set([...existingFilterItemValues]));
        }
        default: {
          return;
        }
      }
    }
  }

  // Если фильтр по ключу из query-параметра является Primitive
  if (
    Array.isArray(availableFilterValues) &&
    availableFilterValues.length &&
    instanceOfFilterPrimitive(availableFilterValues[0])
  ) {
    const availableFilterPrimitiveValues = [
      ...availableFilterValues,
    ] as IFilterPrimitive[];

    // Массив значений фильтра, которые существуют в списке возможных значений фильтра
    const existingFilterPrimitiveValues: PrimitiveType[] = [];

    // Проходимся по возможным значениям фильтра
    // для нахождения значений
    availableFilterPrimitiveValues.forEach(filterPrimitiveValue => {
      // eslint-disable-next-line eqeqeq
      if (filterValues.some(value => value == filterPrimitiveValue.value)) {
        existingFilterPrimitiveValues.push(filterPrimitiveValue.value);
      }
    });
    if (existingFilterPrimitiveValues.length) {
      switch (filterGridItem.type) {
        case FilterBlocksEnum.FilterSelect:
        case FilterBlocksEnum.FilterSlider: {
          return existingFilterPrimitiveValues[existingFilterPrimitiveValues.length - 1];
        }
        case FilterBlocksEnum.FilterMultipleSelect:
        case FilterBlocksEnum.FilterLocation:
        case FilterBlocksEnum.FilterCheckboxGroup:
        case FilterBlocksEnum.FilterModelCheckboxGroup:
        case FilterBlocksEnum.FilterColorPicker: {
          return Array.from(new Set([...existingFilterPrimitiveValues]));
        }
        default: {
          return;
        }
      }
    }
  }

  // Если фильтр по ключу из query-параметра является Filter Range
  if (instanceOfFilterRange(availableFilterValues)) {
    const filterRangeValues = filterValues
      .map(value => Number(value))
      .sort((a, b) => a - b);
    switch (filterGridItem.type) {
      case FilterBlocksEnum.FilterSlider: {
        return filterRangeValues[0];
      }
      case FilterBlocksEnum.FilterRangeSlider: {
        if (!instanceOfRange(filterRangeValues)) {
          return;
        }
        return [
          Math.max(availableFilterValues.min, filterRangeValues[0]),
          Math.min(availableFilterValues.max, filterRangeValues[1]),
        ];
      }
      default: {
        // eslint-disable-next-line no-useless-return
        return;
      }
    }
  }
};

export default takeExistingFilterValue;
